import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RiArrowDownSLine, RiLockFill } from 'react-icons/ri';
import GlassCard from '../ui/v2/GlassCard';
import type { GenerationRules as RulesModel } from '../../models/generation';

interface GenerationRulesProps {
    rules: RulesModel;
    onChange: (rules: RulesModel) => void;
}

export default function GenerationRules({ rules, onChange }: GenerationRulesProps) {
    const [isAdvancedOpen, setIsAdvancedOpen] = useState(false); // Default closed per prompt (gate seniority)

    const toggleContentRule = (key: keyof typeof rules.content_rules) => {
        onChange({
            ...rules,
            content_rules: {
                ...rules.content_rules,
                [key]: !rules.content_rules[key]
            }
        });
    };



    const setSeniority = (level: 'junior' | 'mid' | 'senior') => {
        onChange({
            ...rules,
            experience_alignment: {
                target_level: level
            }
        });
    };

    return (
        <GlassCard>
            <div
                className="flex items-center justify-between cursor-pointer md:cursor-default"
                onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
            >
                <div className="flex items-center gap-2">
                    <h2 className="text-xl font-semibold text-white">Generation Rules</h2>
                </div>
                <RiArrowDownSLine
                    className={`md:hidden text-gray-400 transition-transform ${isAdvancedOpen ? 'rotate-180' : ''}`}
                    size={20}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 mt-6">
                {/* Content Rules */}
                <CheckboxRule
                    label="Highlight quantified impact"
                    description="Emphasize metrics and measurable achievements"
                    checked={rules.content_rules.highlight_impact}
                    onChange={() => toggleContentRule('highlight_impact')}
                />

                {/* Structure Rules (One-page limit removed) */}

                {/* Content Rules: Avoid Fictional */}
                <CheckboxRule
                    label="Avoid adding fictional experience"
                    description="Only use your actual skills and background"
                    checked={rules.content_rules.avoid_fictional_content}
                    onChange={() => toggleContentRule('avoid_fictional_content')}
                />
            </div>

            {/* Advanced Settings */}
            <div className="mt-8 pt-6 border-t border-white/5">
                <button
                    onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
                    className="flex items-center gap-2 text-sm font-medium text-gray-400 hover:text-white transition-colors mb-4 md:mb-4"
                >
                    <RiArrowDownSLine className={`transition-transform ${isAdvancedOpen ? 'rotate-180' : ''}`} />
                    Advanced Settings
                </button>

                <AnimatePresence>
                    {isAdvancedOpen && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                        >
                            <div className="flex flex-col gap-4 pb-2">
                                <div className="flex items-center gap-4">
                                    <span className="text-sm text-gray-500 w-32">Experience Level:</span>
                                    <div className="flex p-1 rounded-lg bg-black/20 border border-white/5">
                                        {['junior', 'mid', 'senior'].map((level) => (
                                            <button
                                                key={level}
                                                onClick={() => setSeniority(level as any)}
                                                className={`
                                                    px-4 py-1.5 rounded-md text-xs font-medium capitalize transition-all
                                                    ${rules.experience_alignment.target_level === level
                                                        ? 'bg-white text-black shadow-[0_0_10px_rgba(255,255,255,0.2)]'
                                                        : 'text-gray-500 hover:text-white'
                                                    }
                                                `}
                                            >
                                                {level}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <p className="text-xs text-gray-600 pl-[9rem]">
                                    Adjusts the tone and terminology to match your target role.
                                </p>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </GlassCard>
    );
}

interface CheckboxProps {
    label: string;
    description: string;
    checked: boolean;
    onChange: () => void;
    disabled?: boolean;
    locked?: boolean;
}

function CheckboxRule({ label, description, checked, onChange, disabled, locked }: CheckboxProps) {
    return (
        <div
            className={`flex items-start gap-3 group ${disabled ? 'cursor-default opacity-80' : 'cursor-pointer'}`}
            onClick={disabled ? undefined : onChange}
        >
            <div className={`
                mt-0.5 w-5 h-5 rounded flex items-center justify-center border transition-all duration-200 shrink-0
                ${checked
                    ? 'bg-white border-white shadow-[0_0_10px_rgba(255,255,255,0.3)]'
                    : 'bg-white/5 border-white/10 group-hover:border-white/20'
                }
                ${locked ? '!bg-gray-800 !border-gray-700 !shadow-none' : ''}
            `}>
                {checked && (
                    <svg className={`w-3.5 h-3.5 ${locked ? 'text-gray-400' : 'text-black'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                )}
            </div>
            <div>
                <div className="flex items-center gap-2">
                    <p className={`text-sm font-medium transition-colors ${checked && !locked ? 'text-white' : 'text-gray-300'}`}>
                        {label}
                    </p>
                    {locked && <RiLockFill className="text-gray-600 text-xs" />}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                    {description}
                </p>
            </div>
        </div>
    );
}
